import {
  type PdfiumApi,
  PdfiumExports as E,
  type PdfiumFunctions,
  type PdfiumTypeMap,
  type Pointer,
} from "@pdfking/pdfium-wrap"
import type { Media, Pixel, SpatialRectangle } from "@pdfking/renderer-api"
import type { Bytes } from "@pdfking/tfunc"

type PointerPage = PdfiumTypeMap["FPDF_PAGE"]

const readBox = async (
  { malloc, call, readFloats }: PdfiumApi,
  page: PointerPage,
  boxType: PdfiumFunctions
): Promise<null | SpatialRectangle<Pixel>> => {
  const { pointer, free } = malloc(16 as Bytes)
  const hasBox = await call(
    boxType,
    page,
    pointer,
    (pointer + 4) as Pointer,
    (pointer + 8) as Pointer,
    (pointer + 12) as Pointer
  )

  if (!hasBox) {
    free()
    return null
  }

  const [left, bottom, right, top] = readFloats(pointer, 4)
  free()

  return {
    x: left as Pixel,
    y: bottom as Pixel,
    width: (right - left) as Pixel,
    height: (top - bottom) as Pixel,
  }
}

export const GetBoxes = async (
  api: PdfiumApi,
  page: PointerPage
): Promise<Media<Pixel>> => {
  const width = (await api.call(E.FPDF_GetPageWidthF, page)) as Pixel
  const height = (await api.call(E.FPDF_GetPageHeightF, page)) as Pixel
  const size = { width, height }

  const defaultBox: SpatialRectangle<Pixel> = {
    x: 0 as Pixel,
    y: 0 as Pixel,
    ...size,
  }

  const media_box =
    (await readBox(api, page, E.FPDFPage_GetMediaBox)) ?? defaultBox
  const trim_box =
    (await readBox(api, page, E.FPDFPage_GetTrimBox)) ?? media_box

  return { size, media_box, trim_box }
}
